import { useCallback, useRef, useState, type ChangeEvent, type ClipboardEvent, type FormEvent } from 'react'
import { X } from 'lucide-react'
import { Modal } from '@/shared/components/Modal'
import { Button, FormInput, FormTextArea } from '@/shared/components/ui'
import { useFormState } from '@/shared/hooks/useFormState'
import { useToast } from '@/shared/hooks/useToast'
import { createBugReport } from '@/shared/lib/api'
import { logger } from '@/shared/lib/logger'

const MAX_IMAGES = 5
const MAX_IMAGE_BYTES = 5 * 1024 * 1024

export interface ReportBugModalProps {
  isOpen: boolean
  onClose: () => void
}

interface AttachedImage {
  file: File
  previewUrl: string
}

export function ReportBugModal({ isOpen, onClose }: ReportBugModalProps) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [images, setImages] = useState<AttachedImage[]>([])
  const [validationError, setValidationError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { showToast } = useToast()

  const resetForm = useCallback(() => {
    setImages((prev) => {
      prev.forEach((img) => URL.revokeObjectURL(img.previewUrl))
      return []
    })
    setTitle('')
    setDescription('')
    setValidationError(null)
  }, [])

  const { isSubmitting, error: submitError, handleSubmit: submitForm } = useFormState({
    onSubmit: async () => {
      await createBugReport({
        title: title.trim(),
        description: description.trim(),
        platform: navigator.userAgent,
        images: images.map((img) => img.file),
      })
    },
    onSuccess: () => {
      showToast('Bug report submitted', 'success')
      resetForm()
      onClose()
    },
  })

  const error = validationError ?? submitError

  const addFiles = useCallback((files: FileList | File[]) => {
    const incoming = Array.from(files).filter((f) => f.type.startsWith('image/'))
    if (incoming.length === 0) return

    setValidationError(null)
    setImages((prev) => {
      const next = [...prev]
      for (const file of incoming) {
        if (next.length >= MAX_IMAGES) {
          setValidationError(`You can attach up to ${MAX_IMAGES} images`)
          break
        }
        if (file.size > MAX_IMAGE_BYTES) {
          setValidationError(`${file.name} is over the 5MB limit`)
          continue
        }
        next.push({ file, previewUrl: URL.createObjectURL(file) })
      }
      return next
    })
  }, [])

  const removeImage = useCallback((index: number) => {
    setImages((prev) => {
      const target = prev[index]
      if (target) URL.revokeObjectURL(target.previewUrl)
      return prev.filter((_, i) => i !== index)
    })
  }, [])

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLFormElement>) => {
      const files = e.clipboardData?.files
      if (!files || files.length === 0) return
      const hasImage = Array.from(files).some((f) => f.type.startsWith('image/'))
      if (!hasImage) return
      e.preventDefault()
      addFiles(files)
    },
    [addFiles]
  )

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(e.target.files)
    e.target.value = ''
  }

  const handleClose = useCallback(() => {
    resetForm()
    onClose()
  }, [resetForm, onClose])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setValidationError(null)

    if (!title.trim()) {
      setValidationError('Title is required')
      return
    }

    try {
      await submitForm(null)
    } catch (err) {
      logger.error('Bug report submission failed', { error: err })
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Report a Bug">
      <form onSubmit={handleSubmit} onPaste={handlePaste} className="space-y-4">
        {error && (
          <div
            role="alert"
            className="bg-red-500/10 border border-red-500/20 text-red-400 px-3 py-2 rounded-lg text-sm"
          >
            {error}
          </div>
        )}

        <FormInput
          id="bug-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          label={
            <span>
              Title <span className="text-red-400">*</span>
            </span>
          }
          placeholder="What went wrong?"
          maxLength={200}
          aria-required="true"
          autoFocus
          disabled={isSubmitting}
        />

        <FormTextArea
          id="bug-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          label="Description"
          placeholder="Steps to reproduce, what you expected, anything else…"
          rows={5}
          maxLength={2000}
          disabled={isSubmitting}
        />

        <div>
          <label
            htmlFor="bug-images"
            className="block text-xs font-medium text-[#888888] uppercase tracking-wider mb-1.5"
          >
            Screenshots{' '}
            <span className="text-[#666666] normal-case font-normal">
              (optional — paste or attach up to {MAX_IMAGES}, 5MB each)
            </span>
          </label>
          <input
            ref={fileInputRef}
            id="bug-images"
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileChange}
            className="hidden"
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isSubmitting || images.length >= MAX_IMAGES}
          >
            Attach image
          </Button>

          {images.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {images.map((img, index) => (
                <div key={img.previewUrl} className="relative group">
                  <img
                    src={img.previewUrl}
                    alt={img.file.name}
                    className="w-16 h-16 object-cover rounded-lg border border-white/10"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    aria-label={`Remove ${img.file.name}`}
                    disabled={isSubmitting}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center rounded-full bg-[#1a1a1a] border border-white/10 text-white/60 hover:text-white hover:bg-red-500/20 transition-colors disabled:opacity-50"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" onClick={handleClose} variant="secondary" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting…' : 'Submit'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
